import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Button, Icon, IconNames, Text, trapFocus as trapFocusHandler } from '@dotcom/crown-ui';
import { DrawerProps, DrawerPositions } from './drawer-types';
import styles from './drawer.module.scss';
import { getDrawerStyles } from './utils/get-drawer-styles';
import { useAppContext } from '../../../context/app-context';
import { SetIsDrawerInteracting } from '../../../context/app-context/app-context-actions';

export const Drawer = ({
  drawerPosition,
  headerContent,
  isOpen,
  id,
  closeButtonText,
  iconButtonTestid = '',
  drawerTestId = '',
  elevation = 16,
  onClose,
  ariaLabel = '',
  role = 'dialog',
  children,
  addClass = '',
  footerContent,
  showVeil = true,
  hideCloseButton,
  closeIconClass = '',
  closeButtonClass = '',
  trapFocus,
  layoutVariant,
  closeIcon = IconNames.Menuclose,
  isFooterSticky,
}: DrawerProps): React.ReactElement => {
  const contentClasses = getDrawerStyles({
    drawerPosition,
    layoutVariant,
    showVeil,
    isOpen,
    addClass,
  });

  const overlayClasses = classNames(styles.drawer, isOpen && styles['is-open'], addClass);

  const drawerRef = useRef<HTMLDivElement>(null);
  const { appDispatch } = useAppContext();

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', close);

    return () => window.removeEventListener('keydown', close);
  }, []);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      SetIsDrawerInteracting(appDispatch, true);
    };

    const handleMouseUp = () => {
      // Clear the flag after a short delay
      setTimeout(() => {
        SetIsDrawerInteracting(appDispatch, false);
      }, 100);
    };

    const drawerElement = drawerRef.current;
    if (drawerElement && isOpen) {
      drawerElement.addEventListener('mousedown', handleMouseDown, true);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        drawerElement.removeEventListener('mousedown', handleMouseDown, true);
        window.removeEventListener('mouseup', handleMouseUp);
        SetIsDrawerInteracting(appDispatch, false);
      };
    }
  }, [isOpen, appDispatch]);

  useEffect(() => {
    if (trapFocus) {
      trapFocusHandler(drawerRef.current);
    }
  }, [trapFocus, drawerRef.current]);

  return (
    <div className={showVeil ? overlayClasses : ''} data-testid={showVeil ? drawerTestId || 'drawer' : ''}>
      {showVeil && (
        <div
          className={styles.veil}
          data-testid="drawer_veil"
          onClick={onClose}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClose();
            }
          }}
        />
      )}
      <div
        id={id}
        data-testid={showVeil ? 'drawer_content' : drawerTestId || 'drawer'}
        className={contentClasses}
        style={{ zIndex: elevation }}
        role={role}
        aria-label={ariaLabel}
        ref={drawerRef}
      >
        <div className={styles.header}>
          {!hideCloseButton && (
            <div
              className={
                drawerPosition === DrawerPositions.Top ? styles['top-drawer-close'] : styles['side-drawer-close']
              }
            >
              <Button
                variant="secondary"
                size="icon"
                onClick={onClose}
                testId={iconButtonTestid ?? 'nav-close-icon'}
                className={classNames(styles['nav-close-icon'], closeButtonClass)}
                aria-label={closeButtonText || 'Close'}
              >
                <Text styleVariant="screen-reader">{closeButtonText}</Text>
                <Icon className={closeIconClass} name={closeIcon} color="purple" />
              </Button>
            </div>
          )}
          {headerContent && <div className={styles.content}>{headerContent}</div>}
        </div>
        <div className={styles.content}>
          <div className={styles['drawer-body']}>{children}</div>
          {footerContent && (
            <div className={`${styles['drawer-footer']} ${isFooterSticky ? styles.sticky : ''}`}>{footerContent}</div>
          )}
        </div>
      </div>
    </div>
  );
};
